import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import PhoneInput from 'react-phone-number-input';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'universal-cookie';
import Swal from 'sweetalert2';

import Button from '../common/Button';
import { notifyAuthChange } from '../../hooks/useAuth';
import { apiUrl } from '../../config/api';
import logo from '../../images/amazing gems and jewellery png with name.png';
import './LoginModal.scss';

const STEP = { DETAILS: 'details', OTP: 'otp', VERIFIED: 'verified' };

/** Checkout without an account: details → email OTP → guest session. */
const GuestLoginModal = ({ show, onClose }) => {
  const [step, setStep] = useState(STEP.DETAILS);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [contactNo, setContactNo] = useState('');
  const [otp, setOtp] = useState('');

  useEffect(() => {
    if (!show) {
      setStep(STEP.DETAILS);
      setOtp('');
      setFormError(null);
    }
  }, [show]);

  const sendOtp = async () => {
    if (!name || !email || !contactNo) {
      setFormError('Please fill in every field before verifying your email.');
      return;
    }

    setLoading(true);
    setFormError(null);
    try {
      const response = await fetch(apiUrl('/api/send-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setFormError(data.message || 'We could not send the code. Try again.');
        return;
      }
      setStep(STEP.OTP);
      setOtp('');
    } catch {
      setFormError('We could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    if (!otp) {
      setFormError('Please enter the code we emailed you.');
      return;
    }

    setLoading(true);
    setFormError(null);
    try {
      const response = await fetch(apiUrl('/api/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setFormError(data.message || 'That code is not correct.');
        setOtp('');
        return;
      }
      setStep(STEP.VERIFIED);
    } catch {
      setFormError('We could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startGuestSession = async (event) => {
    event.preventDefault();
    setLoading(true);
    setFormError(null);

    try {
      const response = await fetch(apiUrl('/api/guest-register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          common_id: localStorage.getItem('common_id') || '',
          name,
          email,
          contact_no: contactNo,
        }),
      });
      const data = await response.json();

      if (data.token && data.user_id) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user_id', data.user_id);
        localStorage.setItem('user_name', data.user_name || name);

        const { exp } = jwtDecode(data.token);
        new Cookies().set('user_id', data.user_id, {
          expires: new Date(exp * 1000),
          path: '/',
        });

        notifyAuthChange();
        onClose();
        Swal.fire({
          icon: 'success',
          confirmButtonColor: '#b63f23',
          title: 'You can continue as a guest.',
        });
      } else {
        setFormError(data.message || 'We could not start a guest session.');
      }
    } catch {
      setFormError('We could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>Continue as guest</Modal.Header>
      <Modal.Body>
        <div className="ij-auth">
          <img className="ij-auth__logo" src={logo} alt="Indian Jewelryish" />

          <form className="ij-auth__form" onSubmit={startGuestSession} noValidate>
            <div className="ij-field">
              <label className="ij-label" htmlFor="guest-name">
                Full name
              </label>
              <input
                id="guest-name"
                type="text"
                className="ij-input"
                autoComplete="name"
                value={name}
                required
                disabled={loading || step !== STEP.DETAILS}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div className="ij-field">
              <label className="ij-label" htmlFor="guest-email">
                Email address
              </label>
              <input
                id="guest-email"
                type="email"
                className="ij-input"
                autoComplete="email"
                value={email}
                required
                disabled={loading || step !== STEP.DETAILS}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div className="ij-field">
              <label className="ij-label" htmlFor="guest-phone">
                Phone number
              </label>
              <PhoneInput
                id="guest-phone"
                placeholder="Enter phone number"
                value={contactNo}
                onChange={setContactNo}
                disabled={loading || step !== STEP.DETAILS}
              />
            </div>

            {formError && (
              <p className="ij-alert ij-alert--error" role="alert">
                {formError}
              </p>
            )}

            {step === STEP.DETAILS && (
              <Button
                variant="primary"
                block
                loading={loading}
                loadingText="Sending code…"
                onClick={sendOtp}
              >
                Verify email
              </Button>
            )}

            {step === STEP.VERIFIED && (
              <>
                <p className="ij-alert ij-alert--success">Email verified.</p>
                <Button
                  type="submit"
                  variant="primary"
                  block
                  loading={loading}
                  loadingText="Continuing…"
                >
                  Continue
                </Button>
              </>
            )}
          </form>

          {step === STEP.OTP && (
            <form className="ij-auth__form" onSubmit={verifyOtp} noValidate>
              <div className="ij-field">
                <label className="ij-label" htmlFor="guest-otp">
                  Enter the code sent to {email}
                </label>
                <input
                  id="guest-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="ij-input"
                  value={otp}
                  required
                  disabled={loading}
                  onChange={(event) => setOtp(event.target.value)}
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                block
                loading={loading}
                loadingText="Verifying…"
              >
                Verify code
              </Button>
              <Button variant="ghost" block disabled={loading} onClick={sendOtp}>
                Resend code
              </Button>
            </form>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default GuestLoginModal;
